import React, { useState, useEffect } from 'react';
import { Plus, Trash2, UserPlus, Shield, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [error, setError] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(null);

  const fetchUsers = () => {
    axios.get(`${API}/admin/users`).then(r => setUsers(r.data.users || [])).catch(() => {});
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.post(`${API}/admin/users`, form);
      toast.success('Admin user created');
      setShowForm(false);
      setForm({ email: '', password: '', name: '' });
      fetchUsers();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to create user');
    }
  };

  const handleDelete = async (email) => {
    setConfirmingDelete(email);
  };

  const executeDelete = async () => {
    if (!confirmingDelete) return;
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(confirmingDelete)}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
    setConfirmingDelete(null);
  };

  return (
    <div data-testid="admin-users">
      {confirmingDelete && <DeleteConfirmModal message={`Delete admin user ${confirmingDelete}?`} onConfirm={executeDelete} onCancel={() => setConfirmingDelete(null)} />}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>Admin Users</h1>
          <p className="text-[#94A3B8] text-sm mt-1">{users.length} administrators</p>
        </div>
        <button
          data-testid="add-user-button"
          onClick={() => { setShowForm(!showForm); setForm({ email: '', password: '', name: '' }); setError(''); }}
          className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] transition-colors rounded-full px-6 py-3 shadow-lg shadow-[#D4AF37]/20 flex items-center gap-2 active:scale-95"
        >
          <UserPlus size={18} /> Add Admin
        </button>
      </div>

      {showForm && (
        <form data-testid="user-form" onSubmit={handleSubmit} className="bg-[#171A21] rounded-2xl border border-white/5 p-6 mb-8 space-y-4">
          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-red-400 text-sm">
              <AlertCircle size={16} /> {error}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Email *</label>
              <input data-testid="user-email-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="admin@example.com" className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600" required />
            </div>
            <div>
              <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Name</label>
              <input data-testid="user-name-input" type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Display name" className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Password *</label>
              <input data-testid="user-password-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Min 6 characters" className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600" required />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="bg-white/5 text-white font-medium hover:bg-white/10 border border-white/10 rounded-full px-6 py-2.5">Cancel</button>
            <button data-testid="user-submit-button" type="submit" className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full px-6 py-2.5">Create Admin</button>
          </div>
        </form>
      )}

      {/* Users List */}
      <div className="space-y-3">
        {users.map((u, i) => {
          const isSelf = u.email === currentUser?.email;
          return (
            <div key={u.email || i} data-testid={`admin-user-${i}`} className="bg-[#171A21] rounded-xl border border-white/5 p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                <span className="text-[#D4AF37] text-lg font-semibold">{(u.name || u.email || '?')[0].toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white font-medium truncate">{u.name || 'Admin'}</p>
                  {isSelf && <span className="text-xs px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] rounded-full font-semibold">You</span>}
                </div>
                <p className="text-[#94A3B8] text-sm truncate">{u.email}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs px-2.5 py-1 bg-white/5 rounded-full text-[#94A3B8] flex items-center gap-1">
                  <Shield size={12} /> {u.role || 'admin'}
                </span>
                {!isSelf && (
                  <button
                    data-testid={`delete-user-${i}`}
                    onClick={() => handleDelete(u.email)}
                    className="p-2 rounded-lg hover:bg-red-500/20 text-[#94A3B8] hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
